import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CatalogResponse, QuoteDetail, QuoteListItem, SaveQuoteInput, SendQuoteInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

export function useCatalog(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'catalog'],
    queryFn: () => api.get<CatalogResponse>(workspacePath(workspaceId!, '/catalog')),
    enabled: !!workspaceId,
    staleTime: 5 * 60_000,
  });
}

export function useQuotes(workspaceId: string | null, params: Record<string, string | undefined>) {
  return useQuery({
    queryKey: [workspaceId, 'quotes', params],
    queryFn: () => api.get<{ items: QuoteListItem[]; nextCursor: string | null; counts?: Record<string, number> }>(workspacePath(workspaceId!, '/quotes'), { query: { ...params, include: 'counts' } }),
    enabled: !!workspaceId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useQuote(workspaceId: string | null, quoteId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'quotes', quoteId],
    queryFn: () => api.get<QuoteDetail>(workspacePath(workspaceId!, `/quotes/${quoteId}`)),
    enabled: !!workspaceId && !!quoteId,
    staleTime: 10_000,
  });
}

export function useSaveQuote(workspaceId: string | null, quoteId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveQuoteInput) =>
      quoteId
        ? api.patch<{ id: string }>(workspacePath(workspaceId!, `/quotes/${quoteId}`), input)
        : api.post<{ id: string }>(workspacePath(workspaceId!, '/quotes'), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes'] });
      void queryClient.invalidateQueries({ queryKey: [workspaceId, 'reports'] });
    },
  });
}

function useInvalidateQuote(workspaceId: string | null, quoteId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes'] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes', quoteId] });
    queryClient.invalidateQueries({ queryKey: [workspaceId, 'reports'] });
  };
}

export function useSendQuote(workspaceId: string | null, quoteId: string) {
  const invalidate = useInvalidateQuote(workspaceId, quoteId);
  return useMutation({
    mutationFn: (input: SendQuoteInput) => api.post<{ shareUrl: string; waLink: string | null; smsText: string | null; smsError: string | null }>(workspacePath(workspaceId!, `/quotes/${quoteId}/send`), input),
    onSuccess: invalidate,
  });
}

export function useVoidQuote(workspaceId: string | null, quoteId: string) {
  const invalidate = useInvalidateQuote(workspaceId, quoteId);
  return useMutation({ mutationFn: () => api.post(workspacePath(workspaceId!, `/quotes/${quoteId}/void`)), onSuccess: invalidate });
}

export function useDeleteQuote(workspaceId: string | null, quoteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(workspacePath(workspaceId!, `/quotes/${quoteId}`)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [workspaceId, 'quotes'] });
      void queryClient.invalidateQueries({ queryKey: [workspaceId, 'reports'] });
    },
  });
}

export function useDuplicateQuote(workspaceId: string | null, quoteId: string) {
  const invalidate = useInvalidateQuote(workspaceId, quoteId);
  return useMutation({ mutationFn: () => api.post<{ id: string }>(workspacePath(workspaceId!, `/quotes/${quoteId}/duplicate`)), onSuccess: invalidate });
}

export function useRevokeLink(workspaceId: string | null, quoteId: string) {
  const invalidate = useInvalidateQuote(workspaceId, quoteId);
  return useMutation({ mutationFn: () => api.post(workspacePath(workspaceId!, `/quotes/${quoteId}/revoke-link`)), onSuccess: invalidate });
}

export function useConvertQuote(workspaceId: string | null, quoteId: string) {
  const invalidate = useInvalidateQuote(workspaceId, quoteId);
  return useMutation({ mutationFn: () => api.post<{ jobId: string }>(workspacePath(workspaceId!, `/quotes/${quoteId}/convert`)), onSuccess: invalidate });
}
